import { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import ReactDOMServer from "react-dom/server";

/*COMPONENT*/
import Modal from "../common_modal/Modal";

/*REDUX*/
import { set_value_sini } from "../../App/actions/sini_v2";
import { set_value_layer } from "../../App/actions/layerActions";
import { get_sini_data } from "../../App/actions/get_sini_data";
import { set_value_properties } from "../../App/actions/propertiesActions";

/*DATA*/
import dict from "../../Data/dict.json";
import uuid from "../../App/validation/uuid";
import domain_list from "../../Data/domain_list";

/*CONST*/
import maplibregl from "maplibre-gl";

const pointer_id = "geojson_gps";

// ─── HARDCODED 3D LAYER IDS ───────────────────────────────────────────────────
const HARDCODED_3D_LAYER_IDS = [
  "3d-station-lawang-sewu",
  "3d-station-monas",
  "3d-station-gedung-sate",
  "3d-station-gelora",
  "3d-station-tasikmalaya",
  "3d-station-masjid-raya-bandung",
  "3d_gereja_blenduk",
  "3d_istora_senayan",
  "3d_monumen_selamat_datang",
  "3d_spiegel",
  "3d_museum_mandala_bhakti",
  "3d_hotel_kotta",
  "3d_suro_boyo",
  "3d_pandanaran",
];
// ──────────────────────────────────────────────────────────────────────────────

const LAYER_GPS = ({
  auth,
  layer,
  properties,
  get_sini_data,
  set_value_sini,
  set_value_layer,
  set_value_properties,
  license_reducer,
}) => {
  const [modal_sini_trigger, set_modal_sini_trigger] = useState(false);

  const map_object = layer?.map_object;
  const gps_object = layer?.gps_object;
  const longitude = gps_object?.longitude;
  const latitude = gps_object?.latitude;
  const basemap_used = properties?.basemap_used;

  const toggle_sini_trigger = useCallback(() => {
    set_modal_sini_trigger((prev) => !prev);
  }, []);

  const on_remove_marker = useCallback(() => {
    set_modal_sini_trigger(false);
    if (map_object) {
      if (map_object.getLayer(pointer_id)) {
        map_object.removeLayer(pointer_id);
      }
      if (map_object.getSource(pointer_id)) {
        map_object.removeSource(pointer_id);
      }
    }
    const marker_element = document.getElementById("gps_map");
    if (marker_element) {
      marker_element.remove();
    }
  }, [map_object]);

  const trigger_sini = useCallback(() => {
    set_value_properties({
      key: "sini_data_mode",
      value: "Site Analysis",
    });

    //cek lisensi di sini

    on_remove_marker();
    const { domain } = auth;
    const show_survey_1 = domain_list?.[domain]?.show_survey_1;
    const params = {
      request_id: uuid(),
      long: longitude,
      lat: latitude,
      km_rad: 1,
      sini_data_mode: "sini_general",
      show_survey_1,
    };
    get_sini_data(params);
    set_value_sini({
      key: "sini_menu_active",
      value: "",
    });
    set_value_sini({
      key: "is_sini_visible",
      value: true,
    });
    set_value_layer({
      key: "statistic_mode",
      value: "sini",
    });
  }, [
    auth,
    get_sini_data,
    latitude,
    longitude,
    on_remove_marker,
    set_value_layer,
    set_value_properties,
    set_value_sini,
  ]);

  // ─── AUTO-SORTER (Safeguard for when 3D models load AFTER Search) ──────────
  useEffect(() => {
    if (!map_object) return;

    const enforceLayerOrder = () => {
      if (!map_object.getStyle()) return;
      const layers = map_object.getStyle().layers;
      if (!layers) return;

      const active3DIds = HARDCODED_3D_LAYER_IDS.filter((id) =>
        map_object.getLayer(id),
      );
      if (active3DIds.length === 0) return;

      let lowest3DIndex = Infinity;
      let lowest3DId = null;
      active3DIds.forEach((id) => {
        const index = layers.findIndex((l) => l.id === id);
        if (index !== -1 && index < lowest3DIndex) {
          lowest3DIndex = index;
          lowest3DId = id;
        }
      });

      if (!lowest3DId) return;

      const searchLayers = [pointer_id];
      searchLayers.forEach((searchId) => {
        const searchIndex = layers.findIndex((l) => l.id === searchId);
        if (searchIndex !== -1 && searchIndex > lowest3DIndex) {
          map_object.moveLayer(searchId, lowest3DId);
        }
      });
    };

    map_object.on("styledata", enforceLayerOrder);
    return () => {
      map_object.off("styledata", enforceLayerOrder);
    };
  }, [map_object]);

  const on_render = useCallback(() => {
    const language = localStorage?.language || "ina";

    if (map_object && longitude && latitude) {
      // ─── BULLETPROOF Z-INDEX FIX ────────────────────────────────────────────
      const activeLayers = map_object.getStyle().layers || [];
      const active3DId = HARDCODED_3D_LAYER_IDS.find((id) =>
        activeLayers.some((l) => l.id === id),
      );

      let beforeId = active3DId;
      if (!beforeId) {
        const firstSymbol = activeLayers.find((l) => l.type === "symbol");
        beforeId = firstSymbol ? firstSymbol.id : undefined;
      }
      // ────────────────────────────────────────────────────────────────────────

      let marker_element = document.getElementById("gps_map");
      if (!marker_element) {
        marker_element = document.createElement("div");
      }
      marker_element.id = "gps_map";
      marker_element.style.position = "absolute";
      marker_element.style.transform = "translate(-50%, -100%)";

      const label_text = dict?.["You're here"]?.[language] || "You're here";

      const marker_jsx = (
        <div
          id="gps_title"
          title={label_text}
          style={{
            position: "relative",
            paddingBottom: "12px",
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        >
          {/* Outer bubble wrapper: Made relative to position the absolute close button */}
          <div
            style={{
              position: "relative",
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.04)",
              padding: "8px 14px",
              maxWidth: "180px",
              textAlign: "center",
            }}
          >
            {/* Close Button UI */}
            <div
              id="gps_close_btn"
              title="Close"
              style={{
                position: "absolute",
                top: "-6px",
                right: "-6px",
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: "#ffffff",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: "bold",
                color: "#666",
                cursor: "pointer",
                zIndex: 10,
              }}
            >
              ✕
            </div>

            {/* Inner text constraint wrapper */}
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "600",
                fontSize: "14px",
                lineHeight: "18px",
                maxHeight: "36px",
                color: "#111111",
              }}
            >
              {label_text}
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "0px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "12px solid #ffffff",
              filter: "drop-shadow(0 3px 2px rgba(0, 0, 0, 0.08))",
              pointerEvents: "none",
            }}
          />
        </div>
      );

      const marker_html = ReactDOMServer.renderToString(marker_jsx);
      marker_element.innerHTML = marker_html;
      const lngLat = new maplibregl.LngLat(longitude, latitude);
      const point = map_object.project(lngLat);
      marker_element.style.left = `${point.x}px`;
      marker_element.style.top = `${point.y}px`;
      map_object.getContainer().appendChild(marker_element);

      // --- NATIVE EVENT LISTENER BINDINGS ---
      const gps_title = document.getElementById("gps_title");
      if (gps_title) {
        gps_title.addEventListener("click", toggle_sini_trigger);
      }

      const gps_close = document.getElementById("gps_close_btn");
      if (gps_close) {
        gps_close.addEventListener("click", (e) => {
          e.stopPropagation(); // Prevents click from bubbling to gps_title
          on_remove_marker();
        });
      }

      map_object.on("move", () => {
        const newPoint = map_object.project(lngLat);
        marker_element.style.left = `${newPoint.x}px`;
        marker_element.style.top = `${newPoint.y}px`;
      });

      const geojson_gps = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [longitude, latitude],
            },
            properties: {},
          },
        ],
      };

      if (!map_object.getSource(pointer_id)) {
        map_object.addSource(pointer_id, {
          type: "geojson",
          data: geojson_gps,
        });
      } else {
        map_object.getSource(pointer_id).setData(geojson_gps);
      }

      if (!map_object.getLayer(pointer_id)) {
        map_object.addLayer(
          {
            id: pointer_id,
            source: pointer_id,
            type: "circle",
            paint: {
              "circle-radius": 5,
              "circle-color": "#0ca5eb", // Kept original GPS blue color
              "circle-stroke-width": 2,
              "circle-stroke-color": "#ffffff",
            },
            layout: { visibility: "visible" },
          },
          beforeId,
        );
      }
    }
  }, [map_object, longitude, latitude, toggle_sini_trigger, on_remove_marker]);

  const on_fly = useCallback(() => {
    if (map_object && longitude && latitude) {
      map_object.flyTo({
        center: [longitude, latitude],
        speed: 1,
        zoom: 18,
      });
    }
  }, [map_object, longitude, latitude]);

  useEffect(() => {
    if (map_object) {
      const on_render_and_fly = () => {
        on_render();
        on_fly();
      };

      if (!map_object.isStyleLoaded()) {
        map_object.once("styledata", on_render_and_fly);
      } else {
        on_render_and_fly();
      }
    }
  }, [basemap_used, gps_object?.uuid, map_object, on_render, on_fly]);

  const language = localStorage?.language || auth.language;
  const { isAuthenticated } = auth;

  const modal_sini_trigger_content = modal_sini_trigger && (
    <Modal
      modalSize="small"
      isOpen={modal_sini_trigger}
      onClose={toggle_sini_trigger}
      title={dict?.["Location Detail"]?.[language] || "Location Detail"}
    >
      <div className="box-body" id="box-body">
        <section className="text_center">
          {isAuthenticated && (
            <>
              <button
                onClick={trigger_sini}
                className="button button_white margin_bottom outline_black"
              >
                {dict?.["Try SINI at your location"]?.[language]}
              </button>
              <br />
            </>
          )}
          <button className="button background_red" onClick={on_remove_marker}>
            {dict?.["Close"]?.[language] || "Close"}
          </button>
        </section>
      </div>
    </Modal>
  );

  return <main>{modal_sini_trigger_content}</main>;
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  layer: state.layer,
  properties: state.properties,
  license_reducer: state.license_reducer,
});

export default connect(mapStateToProps, {
  get_sini_data,
  set_value_sini,
  set_value_layer,
  set_value_properties,
})(LAYER_GPS);
